import { UpsellServiceEnum } from '.spec/self-care/tsTypes';
import { format } from 'date-fns';
import { orderBy } from 'lodash';

import type { UpsellServiceAvailableOffer } from '@types/upsellServiceAvailableOffer';

import {
	INSURANCE_CONTACT_TYPES,
	InsuranceContactSelection,
	InsuranceContactType,
	InsuranceOfferMemory,
	MONTH_KEY_FORMAT,
} from './types';
import { hasExtendedWarranty, monthOfIsoDate, monthsAgoKey, purchaseMonth } from './utils';

const MAX_PURCHASES = 3;
const LAST_CHANCE_MONTHS_AGO = 22;

const withExtendedWarrantyOnly = (
	offer: UpsellServiceAvailableOffer
): UpsellServiceAvailableOffer => ({
	...offer,
	availableServices: offer.availableServices.filter(
		({ service }) => service === UpsellServiceEnum.EXTENDED_WARRANTY
	),
});

function toSelection(
	contactType: InsuranceContactType,
	cohort: UpsellServiceAvailableOffer[]
): InsuranceContactSelection {
	const picked = orderBy(
		cohort,
		['purchasePrice.amount', 'purchaseDate', 'id'],
		['desc', 'desc', 'asc']
	).slice(0, MAX_PURCHASES);

	return {
		contactType,
		purchases:
			contactType === INSURANCE_CONTACT_TYPES.FIRST_CONTACT
				? picked
				: picked.map(withExtendedWarrantyOnly),
	};
}

export function selectInsuranceContact(
	offers: UpsellServiceAvailableOffer[],
	memory: InsuranceOfferMemory,
	now: Date
): InsuranceContactSelection | null {
	const currentMonth = format(now, MONTH_KEY_FORMAT);
	const lastChanceMonth = monthsAgoKey(now, LAST_CHANCE_MONTHS_AGO);
	const reminderMonth = monthsAgoKey(now, 1);
	const monthlyAllowed = memory.lastMonthlyMessageMonth !== currentMonth;

	const lastChance = offers.filter(
		(offer) => purchaseMonth(offer) === lastChanceMonth && hasExtendedWarranty(offer)
	);
	const firstContact = offers.filter((offer) => !(offer.id in memory.contacted));
	const reminder = offers.filter((offer) => {
		if (purchaseMonth(offer) !== reminderMonth || !hasExtendedWarranty(offer)) return false;
		if (!(offer.id in memory.contacted)) return true;
		return monthOfIsoDate(memory.contacted[offer.id]) !== currentMonth;
	});

	if (monthlyAllowed && lastChance.length > 0) {
		return toSelection(INSURANCE_CONTACT_TYPES.LAST_CHANCE, lastChance);
	}
	if (firstContact.length > 0) {
		return toSelection(INSURANCE_CONTACT_TYPES.FIRST_CONTACT, firstContact);
	}
	if (monthlyAllowed && reminder.length > 0) {
		return toSelection(INSURANCE_CONTACT_TYPES.REMINDER, reminder);
	}
	return null;
}
